import { Box, Group, Stack } from "@mantine/core"
import { IconPlus, IconUpload } from "@tabler/icons-react"
import { ButtonWithMenu, Loader } from "@tfg/ui"
import { Dispatch, SetStateAction, useState } from "react"
import { useTranslation } from "react-i18next"
import z from "zod"

import { CountryIso3166Schema, LanguageIso6391 } from "@tfg/types/schemas/enum"
import { standardEmail } from "@tfg/types/schemas/utils"

import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import { CustomTable } from "~/components/table/CustomTable"
import { TABLE_CONTEXT_MAP } from "~/components/table/views/ViewHeader"
import { Views } from "~/components/table/views/Views"
import { useFindContactrMapping } from "~/gen/api/hooks/useFindContactrMapping"
import {
  CreateBusinessPartnerRequestDto,
  createContactBusinessRelationshipRequestDtoTypeEnum,
  FindContactrMappingQueryResponse,
  registrationIdRequestDtoTypeEnum,
  UpdateBusinessPartnerRequestDto,
} from "~/gen/api/types"
import { usePaginatedQuery } from "~/hooks/usePaginatedQuery"
import { SupplierToolBar } from "~/pages/Brand/Suppliers/SuppliersRepository/components/SupplierToolBar"
import { useColumns } from "~/pages/Brand/Suppliers/SuppliersRepository/hooks/columns.tsx"
import { useBuildBusinessPartnerSorts } from "~/pages/Brand/Suppliers/SuppliersRepository/hooks/useBuildBusinessPartnerSorts"
import {
  errorMap,
  StringNoSpecialCharactersSchema,
} from "~/pages/SupplierPortal/TraceabilityRequests/components/ZodCustomErrorMap.tsx"

export type SupplierProps = {
  searchSupplier: string
  setInitialValues: Dispatch<
    SetStateAction<
      CreateBusinessPartnerRequestDto | UpdateBusinessPartnerRequestDto
    >
  >
  setValuesUpdated: Dispatch<SetStateAction<boolean>>
  setFormType: Dispatch<SetStateAction<"create" | "update">>
  setSearchSupplier: Dispatch<SetStateAction<string>>
  openAddSupplierModal: () => void
}

const registrationIdSchema = z.object({
  id: z.string().optional(),
  registrationId: z.string({ error: errorMap }),
  type: z.enum(registrationIdRequestDtoTypeEnum),
})

const businessRelationshipSchema = z.object({
  isActive: z.boolean().default(true),
  supplierCode: z.string().nullable(),
  type: z.enum(createContactBusinessRelationshipRequestDtoTypeEnum).nullable(),
  tagNames: z.array(z.string().max(16)).optional(),
})

export const formSchema = z.object({
  supplierId: z.string().optional(),
  name: StringNoSpecialCharactersSchema,
  email: standardEmail(),
  preferedLangageIso6391: z.enum(LanguageIso6391).default("EN"),
  organization: z.object({
    businessRelationship: businessRelationshipSchema,
    name: z.string({ error: errorMap }).min(2).max(100),
    street: z.string({ error: errorMap }).min(2).max(200),
    city: z.string().min(2).max(200),
    postalCode: z.string({ error: errorMap }).min(2).max(30),
    country: z.enum(CountryIso3166Schema.enum),
    registrationId: z.array(registrationIdSchema).default([]),
  }),
})

export type ContactFormType = z.infer<typeof formSchema>

export function Suppliers({
  setValuesUpdated,
  setInitialValues,
  setFormType,
  searchSupplier,
  setSearchSupplier,
  openAddSupplierModal,
}: SupplierProps) {
  const { t } = useTranslation("organizationSettings")
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState(false)

  const columns = useColumns({
    setInitialValues,
    setValuesUpdated,
    setFormType,
  })
  const { sorts } = useBuildBusinessPartnerSorts()

  const {
    data: suppliersData,
    isPending: isPendingSuppliersQuery,
    isFetching: isFetchingSuppliersQuery,
    setPageBasedPagination,
    pageBasedPagination,
    totalCount,
    totalPages,
  } = usePaginatedQuery<
    typeof useFindContactrMapping,
    FindContactrMappingQueryResponse
  >(useFindContactrMapping, {
    searchSupplier,
    sorts,
  })

  const suppliers = suppliersData?.data ?? []

  const table = useCustomReactTable({
    data: suppliers,
    columns,
    state: {
      pagination: pageBasedPagination,
    },
    pageCount: totalPages,
    manualPagination: true,
    enableRowSelection: true,
    onPaginationChange: setPageBasedPagination,
  })

  return (
    <Stack>
      <Views tableContext={TABLE_CONTEXT_MAP.BUSINESS_PARTNERS_REPOSITORY} />
      <Group justify="space-between">
        <Group justify="left">
          <SupplierToolBar
            table={table}
            isColumnManagerOpen={isColumnManagerOpen}
            setIsColumnManagerOpen={setIsColumnManagerOpen}
            searchSupplier={searchSupplier}
            setSearchSupplier={setSearchSupplier}
          />
        </Group>
        <ButtonWithMenu
          items={[
            {
              label: t("supplierSettings.addSupplier"),
              icon: (
                <Box pr="sm">
                  <IconPlus />
                </Box>
              ),
              onClick: () => openAddSupplierModal(),
            },
            {
              icon: (
                <Box pr="sm">
                  <IconUpload />
                </Box>
              ),
              label: t("supplierSettings.importSuppliers"),
              onClick: () => {
                window.open("/data-transfer/import", "_blank")
              },
            },
          ]}
        >
          {t("supplierSettings.dropdownButton.label")}
        </ButtonWithMenu>
      </Group>
      {isPendingSuppliersQuery ? (
        <Loader fullPage />
      ) : (
        <CustomTable
          table={table}
          manualPaginationCount={totalCount}
          setOpenColumnManager={setIsColumnManagerOpen}
          isFetching={isFetchingSuppliersQuery}
        />
      )}
    </Stack>
  )
}
